import { useTranslation } from 'react-i18next'
import { BadgeCheck, Loader2 } from 'lucide-react'
import { SubscriptionInterval, SUBSCRIPTION_INTERVAL_TRANSLATION_KEYS } from '@/constants/wizard'
import type { StructureOffer } from '@/hooks/useStructureSubscriptions'

interface StructureOfferCardProps {
  offer: StructureOffer
  interval: SubscriptionInterval
  isSelected: boolean
  isCompleted?: boolean
  isLoading?: boolean
  maxLicensesFromOffers?: number | null
  onSelect: () => void
  onGetQuote?: () => void
}

export function StructureOfferCard({
  offer,
  interval,
  isSelected,
  isCompleted = false,
  isLoading = false,
  maxLicensesFromOffers = null,
  onSelect,
  onGetQuote,
}: StructureOfferCardProps) {
  const { t } = useTranslation('wizard')
  const isCustomOffer = offer.id === 'custom-offer'
  const priceLabelKey = SUBSCRIPTION_INTERVAL_TRANSLATION_KEYS[interval]

  const priceForInterval =
    interval === SubscriptionInterval.MONTHLY ? offer.prices.month : offer.prices.year
  const fallbackPrice =
    interval === SubscriptionInterval.MONTHLY ? offer.prices.year : offer.prices.month
  const price = priceForInterval ?? fallbackPrice
  const displayPrice = price ? price.amount / 100 : null

  const discountPercent =
    price?.discount_percent != null && price.discount_percent !== ''
      ? Number(price.discount_percent)
      : null
  const isValidDiscount =
    discountPercent != null &&
    !isNaN(discountPercent) &&
    discountPercent > 0 &&
    discountPercent < 100
  const originalPriceCents =
    isValidDiscount && price ? Math.round(price.amount / (1 - discountPercent / 100)) : null
  const originalPriceDisplay =
    originalPriceCents != null ? (originalPriceCents / 100).toFixed(0) : null

  const licensesNumber = isCustomOffer
    ? maxLicensesFromOffers != null
      ? `>${maxLicensesFromOffers}`
      : '>50'
    : offer.licenses?.toString() || ''
  const licensesText = 'licences'

  return (
    <div
      className={`relative flex h-full flex-col rounded-lg border border-[#DEDEDE] bg-white px-5 py-4 shadow-[0px_3px_8px_0px_rgba(78,44,110,0.07)] transition-all ${
        isSelected ? 'border-primary' : ''
      }`}
    >
      {isCompleted && (
        <div className="absolute -top-3 -right-3">
          <BadgeCheck fill="currentColor" stroke="white" className="h-6 w-6 text-green-500" />
        </div>
      )}

      {isCustomOffer && (
        <div className="mb-7 inline-block w-full self-center rounded-[100px] bg-[#f2cee5] px-3 py-1 text-center text-sm font-medium text-[#7E266B]">
          {t('structureOffers.custom.badge')}
        </div>
      )}

      <div className={`text-left ${isCustomOffer ? 'mb-0' : 'mb-2'}`}>
        <span className="text-[28px] font-bold text-black">{licensesNumber}</span>
        <span className="ml-1 text-sm font-normal text-[#6C677F]">{licensesText}</span>
      </div>

      {!isCustomOffer && (
        <div className="mb-4 text-left">
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-[#6C677F]" />
              <span className="text-sm font-normal text-[#6C677F]">{t('pricing.loading')}</span>
            </div>
          ) : displayPrice !== null ? (
            <>
              {originalPriceDisplay != null && isValidDiscount && (
                <div className="mb-2 flex flex-wrap items-center gap-1">
                  <span className="ml-2 text-xs leading-normal font-normal text-[#6C677F] line-through">
                    {originalPriceDisplay}€
                  </span>
                  <span className="rounded-[134px] bg-[#E9FFF7] px-3 py-1 text-xs font-normal text-[#228C65]">
                    {t('structureOffers.savePercent', { percent: discountPercent })}
                  </span>
                </div>
              )}
              <div className="flex items-baseline gap-1">
                <span className="text-primary text-[17px] font-bold">
                  {displayPrice.toFixed(0)}€
                </span>
                <span className="text-sm font-normal text-[#6C677F]">/ {t(priceLabelKey)}</span>
              </div>
            </>
          ) : null}
        </div>
      )}

      {isCustomOffer && <div className="flex-1" />}

      <div className={`flex flex-col items-center ${isCustomOffer ? 'mt-6 mb-11' : 'mt-0 mb-6'}`}>
        <button
          type="button"
          onClick={onSelect}
          disabled={isLoading || (!isCustomOffer && displayPrice === null)}
          className={`rounded-md px-8 py-2.5 text-sm font-semibold whitespace-nowrap transition-opacity ${
            isLoading || (!isCustomOffer && displayPrice === null)
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : 'bg-primary text-primary-foreground cursor-pointer hover:opacity-90'
          }`}
        >
          {isCustomOffer ? t('structureOffers.custom.contactButton') : t('buttons.selectOffer')}
        </button>
      </div>

      {!isCustomOffer && onGetQuote && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onGetQuote}
            className="cursor-pointer text-sm font-medium text-[#2062F6] underline transition-opacity hover:opacity-80"
          >
            {t('structureOffers.getQuote')}
          </button>
        </div>
      )}
    </div>
  )
}
